import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Stored values, deliberately untranslated.
 *
 * A report that groups "how did you hear about us" has to see one value per
 * answer, not one per answer per language. The guest reads a translated label;
 * the database records the key.
 */
const SEATING = ['non_smoking', 'smoking', 'window', 'quiet', 'family'] as const;
const SOURCES = ['instagram', 'facebook', 'google', 'friend', 'passing', 'returning', 'other'] as const;
import type { Slot, VenueDetail } from '../lib/api.js';
import { DEFAULT_COUNTRY, GCC_COUNTRIES, isValidPhone, toE164 } from '../lib/phone.js';
import { formatTime } from '../lib/time.js';

/**
 * Guest details, and the deposit notice.
 *
 * Asked LAST, once the guest knows they have a table. Collecting a name and
 * phone from someone who is about to be told the evening is full is both rude
 * and a way to accumulate guest records for bookings that never happened.
 *
 * Only first name and phone are required. Every optional field on this form is
 * a chance for a guest on a phone to give up, and a surname is not worth losing
 * a booking over.
 */
/**
 * Where "a large booking" starts, matching the threshold at which the party
 * size step already advises phoning.
 */
const LARGE_PARTY = 8;

export function DetailsStep({
  venue,
  slot,
  partySize,
  locale,
  submitting,
  onBack,
  onSubmit,
}: {
  venue: VenueDetail;
  slot: Slot;
  partySize: number;
  locale: string;
  submitting: boolean;
  onBack: () => void;
  onSubmit: (guest: {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    occasion: string;
    requests: string;
    preferredTable: string;
    seatingPreference: string;
    heardAboutUs: string;
    companyName: string;
    marketingOptIn: boolean;
  }) => void;
}) {
  const { t } = useTranslation();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [country, setCountry] = useState<string>(DEFAULT_COUNTRY);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [occasion, setOccasion] = useState('');
  const [requests, setRequests] = useState('');
  const [preferredTable, setPreferredTable] = useState('');
  const [seatingPreference, setSeatingPreference] = useState('');
  const [heardAboutUs, setHeardAboutUs] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [touchedPhone, setTouchedPhone] = useState(false);

  const venueTime = {
    timezone: venue.timezone,
    serviceDayCutoffHour: 6,
    digitStyle: venue.digit_style,
  };

  const phoneValid = isValidPhone(phone, country as typeof DEFAULT_COUNTRY);
  const canSubmit = firstName.trim().length > 0 && phoneValid && !submitting;

  /**
   * The deposit notice — a possibility, not an invoice.
   *
   * It used to compute and state an exact figure: "A deposit of 400 QAR is
   * required." That number came from the venue's configured policy, and while a
   * seed had invented one it was quoting a real amount to real guests for a
   * charge nobody had decided to take.
   *
   * The restaurant confirms every booking by hand within two hours anyway, so
   * the honest thing to say on the form is that a large party may be asked for
   * one — and to let the person who calls state the actual figure. A number
   * shown here is a promise; a possibility is a warning, which is what a guest
   * needs before they commit.
   *
   * Still shown BEFORE they commit, never after. A deposit discovered on the
   * confirmation screen is a complaint.
   */
  const mayNeedDeposit = partySize >= LARGE_PARTY;

  return (
    <section className="flex flex-col gap-4">
      <div className="rounded-lg bg-brand-teal-light p-3 text-sm">
        <span className="font-medium" data-numeric>
          {formatTime(slot.starts_at, venueTime, locale)}
        </span>
        {' · '}
        <span data-numeric>{partySize}</span>
      </div>

      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (!canSubmit) return;
          onSubmit({
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            phone: toE164(phone, country as typeof DEFAULT_COUNTRY) ?? phone,
            email: email.trim(),
            occasion: occasion.trim(),
            preferredTable: preferredTable.trim(),
            seatingPreference,
            heardAboutUs,
            companyName: companyName.trim(),
            requests: requests.trim(),
            marketingOptIn,
          });
        }}
      >
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">{t('drawer.firstName')}</span>
          <input
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
            required
            autoComplete="given-name"
            className="rounded-lg border border-slate-200 px-3 py-2.5"
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-slate-600">{t('drawer.lastName')}</span>
          <input
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
            autoComplete="family-name"
            className="rounded-lg border border-slate-200 px-3 py-2.5"
          />
        </label>

        <div className="flex flex-col gap-1 text-sm">
          <span className="font-medium">{t('drawer.phone')}</span>
          <div className="flex gap-2">
            <select
              value={country}
              onChange={(event) => setCountry(event.target.value)}
              aria-label={t('drawer.phone')}
              className="rounded-lg border border-slate-200 px-2 py-2.5 font-mono text-sm"
            >
              {GCC_COUNTRIES.map((entry) => (
                <option key={entry.code} value={entry.code}>
                  {`${entry.flag} +${entry.code}`}
                </option>
              ))}
            </select>
            <input
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              onBlur={() => setTouchedPhone(true)}
              required
              inputMode="tel"
              autoComplete="tel"
              aria-invalid={touchedPhone && !phoneValid}
              className="flex-1 rounded-lg border border-slate-200 px-3 py-2.5 font-mono"
            />
          </div>
          {touchedPhone && !phoneValid && phone.length > 0 ? (
            <span className="text-xs text-red-700" role="alert">
              {t('widget.phoneInvalid')}
            </span>
          ) : null}
        </div>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-slate-600">{t('drawer.email')}</span>
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            className="rounded-lg border border-slate-200 px-3 py-2.5"
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-slate-600">{t('drawer.occasion')}</span>
          <input
            value={occasion}
            onChange={(event) => setOccasion(event.target.value)}
            className="rounded-lg border border-slate-200 px-3 py-2.5"
          />
        </label>

        {/* Values are the untranslated keys, so a booking made in Arabic and a
            booking made in English produce the SAME string for a report to
            group on. The label the guest reads is translated; the fact stored
            is not. */}
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-slate-600">{t('widget.seating')}</span>
          <select
            value={seatingPreference}
            onChange={(event) => setSeatingPreference(event.target.value)}
            className="rounded-lg border border-slate-200 px-3 py-2.5"
          >
            <option value="">{t('widget.noPreference')}</option>
            {SEATING.map((key) => (
              <option key={key} value={key}>
                {t(`widget.seating_${key}`)}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-slate-600">{t('widget.heardAboutUs')}</span>
          <select
            value={heardAboutUs}
            onChange={(event) => setHeardAboutUs(event.target.value)}
            className="rounded-lg border border-slate-200 px-3 py-2.5"
          >
            <option value="">{t('widget.selectSource')}</option>
            {SOURCES.map((key) => (
              <option key={key} value={key}>
                {t(`widget.source_${key}`)}
              </option>
            ))}
          </select>
        </label>

        {/* Free text, not a picker. The guest knows their employer's name; we
            do not, and the match happens server-side against the corporate
            accounts list. Leaving it empty is an ordinary booking. */}
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-slate-600">{t('widget.company')}</span>
          <input
            value={companyName}
            onChange={(event) => setCompanyName(event.target.value)}
            className="rounded-lg border border-slate-200 px-3 py-2.5"
          />
          <span className="text-xs text-slate-500">{t('widget.companyHint')}</span>
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-slate-600">{t('widget.preferredTable')}</span>
          <input
            value={preferredTable}
            onChange={(event) => setPreferredTable(event.target.value)}
            inputMode="text"
            className="rounded-lg border border-slate-200 px-3 py-2.5"
          />
          {/* Says "if you have one" out loud. A bare "Table number" field reads
              as required, and a guest who has never been here invents one. */}
          <span className="text-xs text-slate-500">{t('widget.preferredTableHint')}</span>
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-slate-600">{t('drawer.requests')}</span>
          <textarea
            value={requests}
            onChange={(event) => setRequests(event.target.value)}
            rows={2}
            className="rounded-lg border border-slate-200 px-3 py-2.5"
          />
        </label>

        {mayNeedDeposit ? (
          <p className="rounded-lg border border-brand-bronze bg-brand-bronze-light p-3 text-sm">
            {t('widget.depositMayApply')}
          </p>
        ) : null}

        <label className="flex items-start gap-2 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={marketingOptIn}
            onChange={(event) => setMarketingOptIn(event.target.checked)}
            className="mt-0.5"
          />
          <span>{t('widget.marketingOptIn')}</span>
        </label>

        <button
          type="submit"
          data-testid="confirm-booking"
          disabled={!canSubmit}
          className="rounded-lg bg-brand-teal py-3 font-medium text-white hover:bg-brand-teal-dark disabled:opacity-40"
        >
          {submitting ? t('app.loading') : t('widget.confirmBooking')}
        </button>

        <button
          type="button"
          onClick={onBack}
          className="rounded-lg border border-slate-200 py-2.5 text-sm"
        >
          {t('widget.back')}
        </button>
      </form>
    </section>
  );
}
